import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function BrowseTrade() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [forTradeItems, setForTradeItems] = useState([])
  const [myWantedVariantIds, setMyWantedVariantIds] = useState(new Set())
  const [userProfiles, setUserProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showOnlyMyWants, setShowOnlyMyWants] = useState(false)

  useEffect(() => {
    if (!user) return
    fetchData()
  }, [user])

  const fetchData = async () => {
    try {
      const [tradeRes, wantsRes] = await Promise.all([
        supabase
          .from('user_lure_inventory')
          .select(`
            inventory_id, user_id, quantity, variant_id,
            lure_variants (
              variant_id, color_name, variant_name, image_url,
              base_lures (
                base_lure_id, name,
                brands (brand_name)
              )
            )
          `)
          .eq('status_for_trade', true)
          .neq('user_id', user.id)
          .order('inventory_id', { ascending: false }),

        supabase
          .from('user_lure_inventory')
          .select('variant_id')
          .eq('user_id', user.id)
          .or('status_want_in_trade.eq.true,status_want_to_buy.eq.true'),
      ])

      if (tradeRes.error) throw tradeRes.error

      const wantedIds = new Set((wantsRes.data || []).map(w => w.variant_id).filter(Boolean))
      setMyWantedVariantIds(wantedIds)

      const items = tradeRes.data || []
      setForTradeItems(items)

      const userIds = [...new Set(items.map(i => i.user_id))]
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('public_user_profiles')
          .select('id, username')
          .in('id', userIds)
        const map = {}
        profiles?.forEach(p => { map[p.id] = p })
        setUserProfiles(map)
      }
    } catch (err) {
      console.error('Error fetching trade listings:', err)
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    let items = forTradeItems

    if (showOnlyMyWants) {
      items = items.filter(i => myWantedVariantIds.has(i.variant_id))
    }

    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(i => {
        const lureName = i.lure_variants?.base_lures?.name?.toLowerCase() || ''
        const color = (i.lure_variants?.variant_name || i.lure_variants?.color_name || '').toLowerCase()
        const brand = i.lure_variants?.base_lures?.brands?.brand_name?.toLowerCase() || ''
        const uname = userProfiles[i.user_id]?.username?.toLowerCase() || ''
        return lureName.includes(q) || color.includes(q) || brand.includes(q) || uname.includes(q)
      })
    }

    return items
  }, [forTradeItems, showOnlyMyWants, myWantedVariantIds, search, userProfiles])

  if (!user) {
    return (
      <div className="error-page">
        <h2>Login Required</h2>
        <p>You must be logged in to browse items for trade.</p>
        <Link to="/login" className="btn-primary">Log In</Link>
      </div>
    )
  }

  if (loading) return <div className="loading">Loading trade listings...</div>

  return (
    <div className="page-container">
      <div className="page-header-row" style={{ marginBottom: '0.25rem' }}>
        <div>
          <h1>Browse For Trade</h1>
          <p className="page-subtitle">
            {forTradeItems.length} item{forTradeItems.length !== 1 ? 's' : ''} listed for trade by the community
          </p>
        </div>
      </div>

      <div className="trade-browse-filters">
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 240 }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by lure, color, brand, or username..."
            className="color-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="search-clear-btn" title="Clear">✕</button>
          )}
        </div>
        <label className="limited-filter-label" style={{ whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={showOnlyMyWants}
            onChange={e => setShowOnlyMyWants(e.target.checked)}
          />
          <span>Matches My Wants Only</span>
        </label>
      </div>

      <div className="trade-results-info">
        Showing {filtered.length} of {forTradeItems.length} listings
        {showOnlyMyWants && ' · Filtered to your Want in Trade & Want to Buy'}
      </div>

      {filtered.length === 0 ? (
        <div className="no-content-box">
          <p>
            {showOnlyMyWants
              ? 'No trade listings match your Want lists.'
              : 'No items are currently listed for trade.'}
          </p>
          {showOnlyMyWants && (
            <button className="btn-secondary" style={{ marginTop: '0.75rem' }} onClick={() => setShowOnlyMyWants(false)}>
              Show All Listings
            </button>
          )}
        </div>
      ) : (
        <div className="trade-browse-table">
          <div className="trade-browse-header-row">
            <span></span>
            <span>Lure</span>
            <span>Color</span>
            <span>Brand</span>
            <span>Listed by</span>
            <span>Qty</span>
            <span></span>
          </div>
          {filtered.map(item => {
            const v = item.lure_variants
            const lure = v?.base_lures
            const isMatch = myWantedVariantIds.has(item.variant_id)
            const username = userProfiles[item.user_id]?.username || 'Unknown'
            return (
              <div key={item.inventory_id} className={`trade-browse-row${isMatch ? ' trade-match-row' : ''}`}>
                <span className="trade-thumb-cell">
                  {v?.image_url
                    ? <img src={v.image_url} alt={v.color_name} className="trade-thumb" />
                    : <div className="trade-thumb-placeholder" />
                  }
                </span>
                <span className="trade-lure-name-cell">
                  <Link to={`/lure/${lure?.base_lure_id}`} className="trade-lure-link">
                    {lure?.name || '—'}
                  </Link>
                  {isMatch && <span className="trade-match-badge">Matches My Wants</span>}
                </span>
                <span>{v?.variant_name || v?.color_name || '—'}</span>
                <span>{lure?.brands?.brand_name || '—'}</span>
                <span>
                  <Link to={`/profile/${item.user_id}`} className="trade-username-link">
                    {username}
                  </Link>
                </span>
                <span>{item.quantity}</span>
                <span>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => navigate(`/trade/propose/${item.user_id}`)}
                  >
                    Propose Trade
                  </button>
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default BrowseTrade
